// src/PublicOnboarding.jsx
import React, { useState, useEffect } from "react";
import { signInWithGoogle } from "./googleAuth";
import { logFunnelEvent } from "./analytics";

const FEATURES = [
  { icon: "📊", titulo: "Visão completa", desc: "Acompanhe ganhos, gastos, investimentos e doações em um só painel." },
  { icon: "📈", titulo: "Gráficos inteligentes", desc: "Entenda seus hábitos financeiros com clareza, por categoria e por mês." },
  { icon: "🎯", titulo: "Metas e evolução", desc: "Defina objetivos e acompanhe seu progresso ao longo do tempo." },
  { icon: "🔒", titulo: "Segurança", desc: "Seus dados protegidos, com privacidade do início ao fim." },
  { icon: "⭐", titulo: "Tudo em um só lugar", desc: "Organize sua vida financeira de forma prática, sem planilhas." },
];

const PILARES = [
  { cor: "#10b981", label: "Ganhar",       desc: "Controle suas fontes de renda" },
  { cor: "#ef4444", label: "Gastar",       desc: "Acompanhe cada gasto" },
  { cor: "#fbbf24", label: "Guardar",      desc: "Metas e investimentos" },
  { cor: "#a78bfa", label: "Gratidão",     desc: "Valorize o que já tem" },
  { cor: "#60a5fa", label: "Generosidade", desc: "Doe com propósito" },
];

export default function PublicOnboarding({ onCriarConta, onLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(()=>{ logFunnelEvent("onboarding_visto"); },[]);

  async function handleCriarConta() {
    setError("");
    setLoading(true);
    logFunnelEvent("clique_criar_conta");
    const { error: googleError } = await signInWithGoogle();
    setLoading(false);
    if (googleError) { setError(googleError); return; }
    // Sucesso: a troca de sessão no App.jsx já tira essa tela de cena sozinha;
    // aqui só marcamos como "visto" (localStorage) via callback do App.jsx.
    onCriarConta?.();
  }

  return (
    <div style={s.root}>
      <div style={s.page}>
        {/* Header */}
        <div style={s.header}>
          <span style={s.logoText}>Mapa Financeiro 5G</span>
          <button onClick={onLogin} style={s.jaTemConta}>Já tenho conta</button>
        </div>

        <div style={s.content}>

          {/* Hero */}
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <div style={{ fontSize: 32, marginBottom: 6 }}>💰</div>
            <h1 style={s.headline}>Visualize. Organize. Evolua.</h1>
            <p style={s.sub}>
              O <strong>Mapa Financeiro 5G</strong> é um aplicativo de organização financeira pessoal
              que ajuda você a acompanhar suas finanças de forma simples e inteligente, baseado no
              <strong> Método Finanças 5G</strong>.
            </p>
          </div>

          {/* CTAs principais */}
          <button onClick={handleCriarConta} disabled={loading} style={{ ...s.btnCriar, opacity: loading ? 0.6 : 1 }}>
            {loading ? "Entrando..." : "✨ Criar minha conta grátis"}
          </button>
          {error && <div style={s.errorBox}>{error}</div>}
          <p style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", margin: "8px 0 6px" }}>
            Plano gratuito para sempre · 30 dias Premium grátis · Sem cartão
          </p>
          <button onClick={onLogin} style={s.btnLogin}>
            Já tenho conta → Entrar
          </button>

          {/* Pilares 5G */}
          <div style={s.section}>
            <div style={s.sectionLabel}>O método por trás do app</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {PILARES.map(p => (
                <div key={p.label} style={s.pilarRow}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.cor, flexShrink: 0 }} />
                  <span style={{ color: p.cor, fontWeight: 800, fontSize: 13, width: 100, flexShrink: 0 }}>{p.label}</span>
                  <span style={{ color: "#64748b", fontSize: 12 }}>{p.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div style={s.section}>
            <div style={s.sectionLabel}>O que você consegue fazer</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {FEATURES.map(f => (
                <div key={f.titulo} style={s.featureRow}>
                  <div style={s.featureIcon}>{f.icon}</div>
                  <div>
                    <div style={s.featureTitulo}>{f.titulo}</div>
                    <div style={s.featureDesc}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA final */}
          <div style={{ textAlign: "center", margin: "28px 0 16px" }}>
            <p style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", margin: "0 0 14px" }}>
              Seu futuro financeiro começa com as escolhas de hoje.
            </p>
            <button onClick={handleCriarConta} disabled={loading} style={{ ...s.btnCriar, opacity: loading ? 0.6 : 1 }}>
              {loading ? "Entrando..." : "✨ Criar minha conta grátis"}
            </button>
          </div>

          {/* Rodapé com links legais — exigido pelo Google OAuth */}
          <div style={{ textAlign: "center", marginTop: 24, paddingTop: 16, borderTop: "1px solid #e2e8f0" }}>
            <a href="/privacidade.html" style={{ fontSize: 11, color: "#94a3b8", textDecoration: "underline", marginRight: 12 }}>
              Política de Privacidade
            </a>
            <a href="/termos.html" style={{ fontSize: 11, color: "#94a3b8", textDecoration: "underline" }}>
              Termos de Serviço
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh", width: "100%",
    display: "flex", justifyContent: "center",
    background: "radial-gradient(circle at top left,rgba(124,131,255,0.10),transparent 30%),radial-gradient(circle at bottom right,rgba(20,184,166,0.08),transparent 25%),#f8fafc",
    fontFamily: "Inter,ui-sans-serif,system-ui,sans-serif",
  },
  page: { width: "100%", maxWidth: 480, display: "flex", flexDirection: "column" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px" },
  logoText: { fontSize: 13, fontWeight: 700, color: "#10b981" },
  content: { flex: 1, padding: "0 20px 40px", width: "100%", boxSizing: "border-box" },
  headline: { fontSize: 26, fontWeight: 900, color: "#0f172a", lineHeight: 1.25, margin: "0 0 10px", letterSpacing: "-0.02em" },
  sub: { fontSize: 14, color: "#64748b", margin: 0, lineHeight: 1.6 },
  btnCriar: { width: "100%", padding: "15px", borderRadius: 14, background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", fontWeight: 800, fontSize: 16, border: "none", cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 20px rgba(16,185,129,0.35)" },
  btnLogin: { width: "100%", padding: "13px", borderRadius: 14, background: "transparent", border: "2px solid #e2e8f0", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" },
  errorBox: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 12, color: "#b91c1c", padding: "10px 14px", fontSize: 13, margin: "8px 0", textAlign: "center" },
  jaTemConta: { background: "none", border: "none", color: "#94a3b8", fontWeight: 500, fontSize: 13, cursor: "pointer", padding: "4px 8px", fontFamily: "inherit" },
  section: { marginTop: 30 },
  sectionLabel: { fontSize: 11, color: "#94a3b8", marginBottom: 12, textAlign: "center", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 },
  pilarRow: { display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px" },
  featureRow: { display: "flex", alignItems: "flex-start", gap: 12 },
  featureIcon: { fontSize: 22, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, flexShrink: 0 },
  featureTitulo: { fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 2 },
  featureDesc: { fontSize: 12, color: "#64748b", lineHeight: 1.5 },
};
